import React, {useEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import Api from "../../../../../core/services/api/api";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Button from "../../../base/button/button";
import {DialogContent, DialogTitle, Paper} from "@mui/material";
import SearchInput from "../../../base/input/search_input";
import AddVendorModal from "../../../dialogs/add-vendor";
import classnames from "classnames";
import Dialog from "../../../base/dialog";
import {VendorSelectionDataGridCellKeys} from "../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../containers/data-grid";

const initVendorOnlineProfile = {open: false, data: null};


const suggestedVendorsDataGridSaveConfig = {
    name: 'PartSupportVendorSelectionSuggestedVendorsDataGrid',
    version: 1,
}

const vendorsDataGridSaveConfig = {
    name: 'PartSupportVendorSelectionVendorsDataGrid',
    version: 1,
}

const PartSupportVendorSelection = ({
                                        request,
                                        onSelect,
                                        dialog = false,
                                        open,
                                        setOpen,
                                    }) => {
    const [loadingSuggestion, setLoadingSuggestion] = useState(false);
    const [loadingVendors, setLoadingVendors] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [selecting, setSelecting] = useState(false);
    const [vendorOnlineProfile, setVendorOnlineProfile] = useState(initVendorOnlineProfile)
    const isMounted = useIsMounted();

    /**
     * Listens for the changes of request's partNo and with each change:
     * Fetches the suggestions list of the vendor.
     */
    useEffect(() => {
        if (!request?.partNo || (dialog && !open)) {
            return closeDialog();
        }
        getVendorSuggestions().then();
        searchVendors().then();
    }, [request?.partNo, dialog, open])

    /**
     * Fetches the list of vendor suggestions for the provided part number of the request.
     */
    const getVendorSuggestions = async () => {
        setLoadingSuggestion(true);
        const response = await Api.getVendorSuggestions(request?.partNo);
        if (!isMounted())
            return;
        setLoadingSuggestion(false);
        if (response?.resultFlag) {
            setSuggestions(response.data);
        }
    }


    /**
     * Searches for the vendors of the system based on the provided keywords
     * @param text {string}
     */
    const searchVendors = async (text = '') => {
        setLoadingVendors(true);
        const forApi = {
            paginationInfo: {
                currentPage: 1,
                pageSize: 8,
            },
            filters: {
                keyword: text,
            },
        };
        const response = await Api.searchVendors(forApi);
        if (!isMounted())
            return;
        setLoadingVendors(false);
        setVendors(response.data?.resultList ?? []);
    }

    /**
     * Fetches the online profile of the vendor and if it exists, assigns the vendor to the request, otherwise,
     * opens the online creation profile.
     * @param vendor
     */
    const getOnlineProfileAndAssignIfExists = (vendor) => {
        setSelecting(true);
        Api.getVendorInformation(vendor.code).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                // exists
                if (response.data) {
                    assignVendorToRequest(response.data, () => {
                        setSelecting(false);
                    });
                }
                // need to be created first
                else {
                    setVendorOnlineProfile({open: true, data: vendor});
                    setSelecting(false);
                }
            }
        })
    }

    /**
     * Assigns the specified vendor the selected request.
     * @param vendor {any}
     * @param afterApi {function}
     */
    const assignVendorToRequest = (vendor, afterApi) => {
        const forApi = {
            vendor: vendor,
            partSupportReqId: request.id,
        }
        Api.assignVendorToPartSupportRequest(forApi).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                onSelect(response.data);
                if (dialog) closeDialog();
            }
            if (afterApi) afterApi(response);
        })
    }

    /**
     * Closes the dialog, and resets the state values.
     */
    const closeDialog = () => {
        if (setOpen)
            setOpen(false);
        setVendorOnlineProfile(initVendorOnlineProfile);
        setSuggestions([]);
        setVendors([]);
        setSelecting(false);
    }


    const suggestedVendorsColumns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    title: "Vendor Code",
                    name: VendorSelectionDataGridCellKeys.code,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                    width: 200,
                },
                {
                    title: "Vendor Name",
                    name: VendorSelectionDataGridCellKeys.name,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                    width: 200,
                },
                {
                    name: VendorSelectionDataGridCellKeys.select,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                },
            ],
        [])

    const [suggestedVendorsDataGridColumns] = useDataGridSavedState({
        columns: suggestedVendorsColumns,
        name: suggestedVendorsDataGridSaveConfig.name,
        version: suggestedVendorsDataGridSaveConfig.version,
    });

    /**@type {DataGridRow[]}*/
    const suggestedVendorsDataGridRows = useMemo(() => suggestions?.map(suggestion => {
        return ({
            key: suggestion.code,
            data: suggestion,
            cells: {
                [VendorSelectionDataGridCellKeys.code]: suggestion.code,
                [VendorSelectionDataGridCellKeys.name]: suggestion.name,
                [VendorSelectionDataGridCellKeys.select]:
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        disabled={selecting}
                        onClick={() => getOnlineProfileAndAssignIfExists(suggestion)}
                    >
                        {selecting ? 'processing...' : "Select"}
                    </Button>,
            }
        });
    }), [selecting, suggestions])


    const vendorsColumns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    title: "Vendor Code",
                    name: VendorSelectionDataGridCellKeys.code,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                    width: 200,
                },
                {
                    title: "Vendor Name",
                    name: VendorSelectionDataGridCellKeys.name,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                    width: 200,
                },
                {
                    name: VendorSelectionDataGridCellKeys.select,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                },
            ],
        [])

    const [vendorsDataGridColumns] = useDataGridSavedState({
        columns: vendorsColumns,
        name: vendorsDataGridSaveConfig.name,
        version: vendorsDataGridSaveConfig.version,
    });

    /**@type {DataGridRow[]}*/
    const vendorsDataGridRows = useMemo(() => vendors?.map(vendor => {
        return ({
            key: vendor.code,
            data: vendor,
            cells: {
                [VendorSelectionDataGridCellKeys.code]: vendor.code,
                [VendorSelectionDataGridCellKeys.name]: vendor.name,
                [VendorSelectionDataGridCellKeys.select]:
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        disabled={selecting}
                        onClick={() => getOnlineProfileAndAssignIfExists(vendor)}
                    >
                        {selecting ? 'processing...' : "Select"}
                    </Button>,
            }
        });
    }), [selecting, vendors])


    const content = useMemo(() =>
            <>
                <Row>
                    <Col xs={12} className={'mb-4'}>
                        <p className={classnames('text-sm text-color-3', {'mx-3 px-2': dialog})}>
                            Please select a vendor from the <span className={'font-weight-semi-bold black'}>suggestions</span> or <span
                            className={'font-weight-semi-bold black'}>search</span> for it to start a conversation.
                        </p>
                    </Col>
                    <Col xs={12} className={'mb-5'}>
                        <Paper className={classnames('paper px-4 py-3 transition', {'no-shadow bg-transparent': dialog})}>
                            <Row className={'no-gutters w-100'}>
                                <p className={'text-md font-weight-semi-bold black mb-4'}>
                                    Suggested Vendors
                                </p>
                                <PartnerDataGrid
                                    {...suggestedVendorsDataGridSaveConfig}
                                    initialLayoutState={{
                                        columns: suggestedVendorsColumns,
                                    }}
                                    columns={suggestedVendorsDataGridColumns}
                                    rows={suggestedVendorsDataGridRows}
                                    hideFooter
                                    hideToolbar
                                    state={{
                                        loading: {state: loadingSuggestion}
                                    }}
                                    classNames={{
                                        layout: 'mt-0'
                                    }}
                                />
                            </Row>
                        </Paper>
                    </Col>
                    <Col xs={12} className={'mb-5'}>
                        <Paper className={classnames('paper px-4 py-3 transition', {'no-shadow bg-transparent': dialog})}>
                            <Row className={'no-gutters w-100'}>
                                <p className={'text-md font-weight-semi-bold black'}>
                                    Search for Vendors
                                </p>
                                <Col xs={12} className='px-3'>
                                    <SearchInput
                                        paperProps={{className: 'px-0 mt-2'}}
                                        loading={loadingVendors}
                                        autoSearch
                                        search={searchVendors}
                                        initialValue={''}
                                        label={'Search'}
                                        variant={'outlined'}
                                    />
                                </Col>
                                <div className='w-100 my-2'/>
                                <PartnerDataGrid
                                    {...vendorsDataGridSaveConfig}
                                    initialLayoutState={{
                                        columns: vendorsColumns,
                                    }}
                                    columns={vendorsDataGridColumns}
                                    rows={vendorsDataGridRows}
                                    hideFooter
                                    hideToolbar
                                    state={{
                                        loading: {state: loadingVendors}
                                    }}
                                    classNames={{
                                        layout: 'mt-0'
                                    }}
                                />
                            </Row>
                        </Paper>
                    </Col>
                </Row>
            </>
        , [dialog, suggestedVendorsColumns, suggestedVendorsDataGridColumns, suggestedVendorsDataGridRows, loadingSuggestion, loadingVendors, vendorsColumns, vendorsDataGridColumns, vendorsDataGridRows])

    return (
        <>
            {
                dialog
                    ? <Dialog open={open} setOpen={setOpen} maxWidth={'md'} className={'bound-dialog'}>
                        <DialogTitle>
                            <Row xs={12} className='no-gutters justify-content-center w-100'>
                                <p className={`text-lg text-color-1 my-4 font-weight-semi-bold text-center`}>
                                    Select Vendor
                                </p>
                            </Row>
                        </DialogTitle>
                        <DialogContent dividers={true}>
                            <div className={'px-2 mt-4'}>
                                {content}
                            </div>
                        </DialogContent>
                    </Dialog>
                    : content
            }
            <AddVendorModal
                addVendor={assignVendorToRequest}
                initialValues={vendorOnlineProfile.data}
                open={vendorOnlineProfile.open}
                onBack={() => setVendorOnlineProfile(initVendorOnlineProfile)}
                setOpen={() => setVendorOnlineProfile(initVendorOnlineProfile)}
            />
        </>
    );
}

export default PartSupportVendorSelection;
